/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { mapBy, filterBy, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { uniq } from '@linc-technologies/linced-common/utils/arrays';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

export default class FinanceController extends Controller {
  @service
  session;

  @service
  store;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @alias('session.school')
  school;

  @computed('model.{accounts.@each.person_id,person.id}')
  get learnerAccounts() {
    return this.model.accounts.filter(
      (item) => item.person_id === this.get('model.person.id'),
    );
  }

  @computed('learnerAccounts.[]')
  get account() {
    return this.learnerAccounts[0];
  }

  // readOnlyContactAccess determines whether contacts' finance access should
  // be read-only (i.e. NSW, where it's synced via SAIS).
  @computed(
    'jurisdiction',
    'account.person.learner.contacts.@each.hasCustodyOrder',
  )
  get readOnlyContactAccess() {
    switch (this.jurisdiction) {
      case Jurisdiction.AU_NSW:
        if (isEmpty(this.get('account.person.learner.contacts'))) {
          // If there are no contacts, it doesn't really matter.
          return true;
        }
        if (!isEmpty(this.get('account.person.accessArrangements'))) {
          // NSW schools can (and must) manage Hero access for students with
          // custody orders and/or Legal Dossiers, as we don't have sufficient
          // information to safely determine who should have access in these
          // situations.  The "access arrangements" field is read-only, and
          // a generated note is added to it if a Legal Dossier is present.
          return false;
        }
        // Contact access should be read-only unless there's a custody order.
        return this.get('account.person.learner.contacts').every(
          (contact) => !contact.hasCustodyOrder,
        );
    }
    return false;
  }

  // primaryContacts also includes secondary contacts for NSW schools, to
  // allow them to manage finance access in families with court orders.
  @computed(
    'jurisdiction',
    'model.person.learner.{primaryContacts.[],secondaryContacts.[]}',
  )
  get primaryContacts() {
    let contacts = this.get('model.person.learner.primaryContacts');
    switch (this.jurisdiction) {
      case Jurisdiction.AU_NSW:
        // primaryContacts is a plain array from a getter: copy, don't mutate.
        contacts = [
          ...contacts,
          ...this.get('model.person.learner.secondaryContacts'),
        ];
        break;
    }
    return uniq(contacts);
  }

  @mapBy('primaryContacts', 'caregiverId')
  primaryContactCaregiverIds;

  @filterBy('learnerAccounts', 'notes')
  accountsWithNotes;

  @tracked
  accountSettingsDialogOpen = false;

  @tracked
  accountSettingsSaveInProgress;

  @tracked
  errorMessage;

  @action
  openAccountSettingsDialog() {
    this.accountSettingsDialogOpen = true;
  }

  @action
  saveAccountSettings() {
    this.accountSettingsSaveInProgress = true;
    let promises = [];
    this.learnerAccounts.forEach((account) => {
      promises.push(account.save());
    });
    Promise.all(promises)
      .then(() => {
        this.accountSettingsSaveInProgress = false;
        this.accountSettingsDialogOpen = false;
      })
      .catch(() => {
        this.accountSettingsSaveInProgress = false;
        this.errorMessage =
          'Error saving account settings.  Please check your internet connection and try again.';
      });
  }

  @action
  closeAccountSettingsDialog() {
    this.model.accounts.forEach((account) => {
      rollbackRecord(account);
    });
    this.accountSettingsDialogOpen = false;
  }

  @action
  extraCaregivers(caregivers) {
    return caregivers.filter((caregiver) => {
      return !this.primaryContactCaregiverIds.includes(caregiver.id);
    });
  }

  @action
  async toggleContactAccess(account, contact, checked) {
    let contacts = await account.caregiverIds;
    let caregiver = this.store.peekRecord('person', contact.caregiverId);
    let index = contacts.indexOf(caregiver);

    if (checked && index === -1) {
      contacts.push(caregiver);
    } else if (!checked && index !== -1) {
      contacts.splice(index, 1);
    }
  }

  @action
  changeAddressType(account, newType) {
    let newId = '';
    switch (newType) {
      case 'learner':
        // Set to learner
        newId = this.get('model.person.id');
        break;
      case 'caregiver':
        // If the account has any caregivers with access, pick the first one who's still a primary contact.
        // Otherwise, set to learner's first primary contact.
        newId = this.get('primaryContacts.0.caregiverId');
        if (account.caregivers.length) {
          let caregiver = account.caregiver_ids.find((caregiverId) => {
            return this.primaryContacts.find(
              (item) => item.caregiverId === caregiverId,
            );
          });
          if (!isEmpty(caregiver)) {
            newId = caregiver;
          }
        }
        break;
    }

    account.set('addressPersonId', newId);
  }

  @action
  setAddressCaregiver(account, contact) {
    account.set('addressPersonId', contact.caregiverId);
  }
}
